<template>
  <div>
    <b-form-row>
      <b-col>
        <b-form-group
          v-if="externalSurveyInstanceId"
          :label="$t('Webportal.SurveyInstanceId')"
          label-size="sm"
        >
          <span class="font-weight-bold">{{ externalSurveyInstanceId }}</span>

          <template v-if="surveyRevision && relatedSurveyRevisionsVisible">
            <b-button
              id="popover-related-survey-revisions"
              class="ml-2"
              size="sm"
              variant="outline-primary">
              <font-awesome-icon :icon="['fas', 'link']" />
            </b-button>
            <b-popover
              target="popover-related-survey-revisions"
              triggers="hover focus"
              placement="bottom"
              custom-class="mw-100"
              :title="$t('Webportal.Actions.RelatedSurveyRevisions')">
              <b-list-group v-if="surveyRevision.RelatedSurveyRevisions && surveyRevision.RelatedSurveyRevisions.length > 0" flush>
                <b-list-group-item
                  v-for="(relatedSurveyRevision, index) in surveyRevision.RelatedSurveyRevisions"
                  :key="index">
                  <b-row>
                    <b-col cols="auto">
                      <b-link :to="{ name: 'SurveyRevision', params: { id: relatedSurveyRevision.SurveyRevisionId } }" target="_blank">
                        #{{ relatedSurveyRevision.SurveyRevisionVisibleId }}
                      </b-link>
                    </b-col>
                    <b-col cols="auto">
                      {{ relatedSurveyRevision.LastUpdatedAt | msm_datetime }}
                    </b-col>
                    <b-col>
                      <span class="font-weight-bold">
                        <font-awesome-icon
                          :icon="['far', getRevisionStatusIcon(relatedSurveyRevision.StatusId)]"
                        />
                        {{ getRevisionStatusText(relatedSurveyRevision.StatusId) }}
                      </span>
                    </b-col>
                  </b-row>
                </b-list-group-item>
              </b-list-group>
              <span v-else>
                {{ $t("Webportal.NoDataFoundPlain") }}
              </span>
            </b-popover>
          </template>
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group
          v-if="surveyInstance"
          :label="$t('Webportal.Survey')"
          label-size="sm"
        >
          {{ surveyInstance.Survey.Title }}
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group
          v-if="statusId != null"
          :label="$t('Webportal.Status')"
          label-size="sm"
        >
          <b-dropdown
            v-if="isStatusSelectionEnabled"
            size="sm"
            variant="primary"
            link-class="font-weight-bold"
          >
            <template #button-content>
              <span class="font-weight-bold">
                <font-awesome-icon
                  :icon="['far', getRevisionStatusIcon(statusId)]"
                />
                {{ getRevisionStatusText(statusId) }}
              </span>
            </template>
            <b-dropdown-item
              v-for="nextStatus in surveyRevision.NextAllowedSurveyRevisionStatuses"
              :key="nextStatus"
              :variant="
                nextStatus == SurveyRevisionStatus.Dismissed ? 'danger' : null
              "
              @click="nextStatusClicked(nextStatus)"
            >
              <font-awesome-icon
                :icon="['far', getRevisionStatusIcon(nextStatus)]"
              />
              {{ getRevisionStatusText(nextStatus) }}
            </b-dropdown-item>
          </b-dropdown>

          <span v-else class="font-weight-bold">
            <template v-if="surveyRevision">
              <font-awesome-icon
                :icon="['far', getRevisionStatusIcon(statusId)]"
              />
              {{ getRevisionStatusText(statusId) }}
            </template>
            <template v-else>
              {{ getSurveyInstanceStatusText(statusId) }}
            </template>
          </span>

          <template v-if="surveyRevision && statusHistoryVisible">
            <b-button
              id="popover-status-history"
              class="ml-2"
              size="sm"
              variant="outline-primary"
            >
              <font-awesome-icon :icon="['fas', 'history']" />
            </b-button>
            <b-popover
              target="popover-status-history"
              triggers="hover focus"
              placement="bottom"
              custom-class="mw-100"
              :title="$t('Webportal.StatusHistory')"
            >
              <b-list-group
                v-if="
                  surveyRevision.History && surveyRevision.History.length > 0
                "
                flush
              >
                <b-list-group-item
                  v-for="(history, index) in surveyRevision.History"
                  :key="index"
                >
                  <b-row>
                    <b-col>
                      <span class="font-weight-bold">
                        <font-awesome-icon
                          :icon="[
                            'far',
                            getRevisionStatusIcon(history.StatusId),
                          ]"
                        />
                        {{ getRevisionStatusText(history.StatusId) }}
                      </span>
                    </b-col>
                  </b-row>
                  <b-row>
                    <b-col cols="auto">
                      {{ history.ModifiedAt | msm_datetime }}
                    </b-col>
                    <b-col>
                      {{
                        `${history.ModifiedBy.FirstName} ${history.ModifiedBy.LastName}` +
                        (history.ModifiedBy.Email
                          ? ` (${history.ModifiedBy.Email})`
                          : "")
                      }}
                    </b-col>
                  </b-row>
                </b-list-group-item>
              </b-list-group>
              <span v-else>
                {{ $t("Webportal.NoDataFoundPlain") }}
              </span>
            </b-popover>
          </template>
        </b-form-group>
      </b-col>
    </b-form-row>

    <b-form-row v-if="surveyInstance">
      <b-col>
        <b-form-row>
          <b-col>
            <b-form-group :label="$t('Webportal.Customer')" label-size="sm">
              {{ surveyInstance.Client.Name }}
            </b-form-group>
          </b-col>
          <b-col>
            <b-form-group :label="$t('Webportal.LocationId')" label-size="sm">
              {{ surveyInstance.ClientLocation.ClientLocationIdOnClientSite }}
            </b-form-group>
          </b-col>
        </b-form-row>
        <p v-if="!surveyRevision && notificationHint">
          <span class="text-danger">
            <font-awesome-icon :icon="['fas', 'info-circle']" /> {{ notificationHint }}:
          </span>
          <b-link :to="notificationSettingsRoute">{{ $t("Webportal.NotificationSettings") }}</b-link>
        </p>
      </b-col>
      <b-col cols="4">
        <b-form-group :label="$t('Webportal.LocationAddress')" label-size="sm">
          {{ surveyInstance.ClientLocation.Name }}<br />
          {{ surveyInstance.ClientLocation.Address }}<br />
          <span v-if="surveyInstance.ClientLocation.Address2">
            {{ surveyInstance.ClientLocation.Address2 }}
            <br />
          </span>
          {{ surveyInstance.ClientLocation.City }},
          {{ surveyInstance.ClientLocation.CountryId }} -
          {{ surveyInstance.ClientLocation.PostalCode }}
        </b-form-group>
      </b-col>
    </b-form-row>
  </div>
</template>

<script>
import SurveyRevisionMixin from "@/mixins/SurveyRevisionMixin";

export default {
  name: "SurveyRevisionHeader",

  mixins: [SurveyRevisionMixin],

  props: {
    surveyRevision: null,
    surveyInstance: null,
    statusSelection: { default: false },
    statusHistoryVisible: { default: true },
    relatedSurveyRevisionsVisible: { default: true },
  },

  computed: {
    externalSurveyInstanceId() {
      return (
        this.surveyRevision?.ExternalSurveyInstanceId ??
        this.surveyInstance?.ExternalSurveyInstanceId ??
        this.surveyInstance?.SurveyInstance?.ExternalSurveyInstanceId
      );
    },

    statusId() {
      return (
        this.surveyRevision?.StatusId ??
        this.surveyInstance?.SurveyInstance?.SurveyInstanceStatusId
      );
    },

    isStatusSelectionEnabled() {
      return (
        this.statusSelection && !_.isEmpty(this.surveyRevision?.NextAllowedSurveyRevisionStatuses)
      );
    },

    notificationHint() {
      return this.$t('Webportal.Actions.SurveyRevision.NotificationNotice');
    }
  },

  methods: {
    onStatusChanged(value) {
      this.$emit("statusChanged", value);
    },

    nextStatusClicked(nextStatusId) {
      // Show confirmation dialog before setting final status
      if (
        nextStatusId == this.SurveyRevisionStatus.Approved ||
        nextStatusId == this.SurveyRevisionStatus.Revised ||
        nextStatusId == this.SurveyRevisionStatus.Done ||
        nextStatusId == this.SurveyRevisionStatus.Dismissed ||
        nextStatusId == this.SurveyRevisionStatus.RevisedApproved ||
        nextStatusId == this.SurveyRevisionStatus.RevisedDismissed
      ) {
        const h = this.$createElement;

        this.$bvModal
          .msgBoxConfirm(
            [
              h("div", {
                domProps: {
                  innerHTML: this.$t(
                    "Webportal.Dialog.ConfirmStatusChange.Text",
                    {
                      status: `<b>${this.getRevisionStatusText(
                        nextStatusId
                      )}</b>`,
                    }
                  ),
                },
              }),
            ],
            {
              title: this.$t("Webportal.Dialog.ConfirmStatusChange.Title"),
              okTitle: this.$t("Webportal.Dialog.Ok"),
              cancelTitle: this.$t("Webportal.Dialog.Cancel"),
              hideHeaderClose: false,
            }
          )
          .then((value) => {
            if (value) {
              this.onStatusChanged(nextStatusId);
            }
          });
      } else this.onStatusChanged(nextStatusId);
    },
  },
};
</script>
